import { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import styles from "../styles/CreatorDashboard.module.scss";
import api from "../../../utils/api";
import { RecentOrdersResponse } from "../types/creator.types";
import Spinner from "../../../components/Spinner";


const salesData = [
  { date: "Feb 1", sales: 2700 },
  { date: "Feb 2", sales: 1650 },
  { date: "Feb 3", sales: 3050 },
  { date: "Feb 4", sales: 2850 },
  { date: "Feb 5", sales: 4350 },
  { date: "Feb 6", sales: 3250 },
  { date: "Feb 7", sales: 4650 },
  { date: "Feb 8", sales: 4450 },
  { date: "Feb 9", sales: 5900 },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <p className={styles.tooltipLabel}>{label}</p>
      <p className={styles.tooltipValue}>${payload[0].value.toLocaleString()}</p>
    </div>
  );
};


const CreatorDashboard = () => {
  const [stats, setStats] = useState<any>(null);
  const [recentOrders, setRecentOrders] = useState<RecentOrdersResponse>([]);
  const [loading, setLoading] = useState(false);

  const metrics = [
    {
      label: "Total Sales",
      value: stats ? `$${stats.totalSales.toLocaleString()}` : "$0",
      change: "+12.5%",
      sub: "Last 7 days",
      iconBg: "#6B3FA0",
      icon: (
        <svg width="18" height="18" fill="white" viewBox="0 0 24 24">
          <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96C5 16.1 6.9 18 9 18h12v-2H9.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63H19c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1 1 0 0023.46 5H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
        </svg>
      ),
    },
    {
      label: "Gross Revenue",
      value: "$45,231",
      change: "+8.2%",
      sub: "Last 30 days",
      iconBg: "#C9951A",
      icon: (
        <svg width="18" height="18" fill="white" viewBox="0 0 24 24">
          <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
        </svg>
      ),
    },
    {
      label: "Available Balance",
      value: "$12,450",
      change: null,
      sub: "Ready for payout · Min $50",
      iconBg: "#E8EEF2",
      icon: (
        <svg width="18" height="18" fill="#5B7E8C" viewBox="0 0 24 24">
          <path d="M21 7.28V5c0-1.1-.9-2-2-2H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-2.28A2 2 0 0022 15V9a2 2 0 00-1-1.72zM20 9v6h-7V9h7zM5 19V5h14v2h-6c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h6v2H5z" />
          <circle cx="16" cy="12" r="1.5" fill="#5B7E8C" />
        </svg>
      ),
    },
    {
      label: "Orders",
      value: stats?.totalOrders || "0",
      change: "+18.4%",
      sub: "Last 7 days",
      iconBg: "#F0F0F0",
      icon: (
        <svg width="18" height="18" fill="#888" viewBox="0 0 24 24">
          <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
        </svg>
      ),
    },
  ];

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const response = await Promise.all([
          api.get('/creators/dashboard/stats'),
          api.get('/creators/orders/recent')
        ]);
        const stats = await response[0].data;
        const recentOrders = await response[1].data;
        setRecentOrders(recentOrders);
        setStats(stats);
      }
      catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();

  }, [])

  if (loading) {
    return <Spinner />
  }
  return (
    <div className={styles.dashboard}>

      {/* ── Main ── */}
      <main className={styles.main}>
        <h1 className={styles.pageTitle}>Dashboard</h1>
        <p className={styles.pageSubtitle}>
          Welcome back! Here's what's happening with your store today.
        </p>

        {/* Metric Cards */}
        <div className={styles.metricsGrid}>
          {metrics.map((m) => (
            <div key={m.label} className={styles.metricCard}>
              <div>
                <p className={styles.metricLabel}>{m.label}</p>
                <p className={styles.metricValue}>{m.value}</p>
                {m.change && <p className={styles.metricChange}>↗ {m.change}</p>}
                <p className={styles.metricSub}>{m.sub}</p>
              </div>
              <div className={styles.metricIcon} style={{ background: m.iconBg }}>
                {m.icon}
              </div>
            </div>
          ))}
        </div>

        {/* Sales Chart */}
        <div className={styles.card}>
          <p className={styles.cardTitle}>Sales Overview</p>
          <p className={styles.cardSubtitle}>Daily sales for the last 9 days</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={salesData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6B3FA0" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#6B3FA0" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#aaa" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#aaa" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v === 0 ? "0" : `${(v / 1000).toFixed(1)}k`)}
                domain={[0, 6500]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#6B3FA0"
                strokeWidth={2}
                fill="url(#salesGrad)"
                dot={{ fill: "#6B3FA0", r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: "#6B3FA0" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Orders */}
        <div className={styles.card}>
          <p className={styles.cardTitle}>Recent Orders</p>
          <p className={styles.cardSubtitle}>Your latest transactions</p>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Order ID</th>
                <th className={styles.th}>Date</th>
                <th className={styles.th}>Product</th>
                <th className={`${styles.th} ${styles.alignRight}`}>Value</th>
                <th className={`${styles.th} ${styles.alignRight}`}>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((o) => (
                <tr key={o._id} className={styles.tr}>
                  <td className={`${styles.td} ${styles.orderId}`}>{o._id}</td>
                  <td className={`${styles.td} ${styles.date}`}>{o.createdAt}</td>
                  <td className={styles.td}>{o.orderItems[0]?.name}</td>
                  <td className={`${styles.td} ${styles.value}`}>${o.totalPrice.toFixed(2)}</td>
                  <td className={`${styles.td} ${styles.status}`}>
                    <span
                      className={`${styles.badge} ${o.orderStatus === "completed" ? styles.completed : styles.pending
                        }`}
                    >
                      {o.orderStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

export default CreatorDashboard;